import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MapPin, ChevronLeft, ArrowRight, Plus, Pencil } from 'lucide-react';

export default function AlphabetSelectorModal({
  open, onOpenChange,
  step, setStep,
  selectedLetter, setSelectedLetter,
  selectedContact, setSelectedContact,
  selectedPostcode, setSelectedPostcode,
  alphabetNote, setAlphabetNote,
  getContactsByLetter,
  destinations,
  onConfirm,
  resetSelector,
  isHistoryMode = false,
}) {
  const [manualName, setManualName] = useState('');
  const [manualPostcode, setManualPostcode] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  const goManual = () => {
    setManualName('');
    setManualPostcode('');
    setManualPhone('');
    setStep(5);
  };

  const submitManual = () => {
    const name = manualName.trim();
    const pc = manualPostcode.trim().toUpperCase();
    if (!name || !pc) return;
    onConfirm({ contact: { name, postcodes: [pc], postcode: pc }, postcode: pc, note: alphabetNote, phone: manualPhone.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetSelector(); onOpenChange(o); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-md p-0 gap-0 overflow-hidden">
        {/* Step 1: Alphabet Grid */}
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="p-4 border-b border-[rgba(0,0,0,0.08)]">
              <DialogTitle className="text-lg font-semibold text-[#1d1d1f]">Select a Letter</DialogTitle>
              <p className="text-sm text-[#86868b] mt-1">Tap a letter to find contacts</p>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-9 gap-1.5">
                {(() => {
                  const letterMap = getContactsByLetter();
                  return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
                    const count = letterMap[letter]?.length || 0;
                    const isActive = count > 0;
                    return (
                      <button key={letter} onClick={() => { if (isActive) { setSelectedLetter(letter); setStep(2); } }} disabled={!isActive}
                        className={`aspect-square rounded-lg text-sm font-semibold relative transition-all duration-200 ${isActive ? 'bg-[#007aff] text-white hover:bg-[#0066d6] active:scale-95' : 'bg-[#f5f5f7] text-[#c7c7cc] cursor-not-allowed'}`}>
                        {letter}
                        {count > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#ff3b30] text-white text-[10px] rounded-full flex items-center justify-center font-bold">{count}</span>}
                      </button>
                    );
                  });
                })()}
              </div>
              <Button
                variant="outline"
                onClick={goManual}
                className="mt-4 w-full h-11 text-sm font-semibold rounded-xl"
                data-testid="alphabet-manual-entry-btn"
              >
                <Pencil className="w-4 h-4 mr-2" />Enter manually (not in contacts)
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Names List */}
        {step === 2 && selectedLetter && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="p-4 border-b border-[rgba(0,0,0,0.08)] flex items-center gap-3">
              <button onClick={() => { setSelectedLetter(null); setStep(1); }} className="p-2 -ml-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"><ChevronLeft className="w-5 h-5 text-[#007aff]" /></button>
              <div>
                <DialogTitle className="text-lg font-semibold text-[#1d1d1f]">{selectedLetter}</DialogTitle>
                <p className="text-sm text-[#86868b]">{getContactsByLetter()[selectedLetter]?.length || 0} contacts</p>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {getContactsByLetter()[selectedLetter]?.map((contact, idx) => {
                const postcodes = contact.postcodes || [contact.postcode];
                return (
                  <button key={contact.id || idx} onClick={() => { setSelectedContact(contact); if (postcodes.length === 1) { setSelectedPostcode(postcodes[0]); setStep(4); } else { setStep(3); } }}
                    className="w-full p-4 flex items-center gap-3 hover:bg-[#f5f5f7] transition-colors border-b border-[rgba(0,0,0,0.06)] last:border-0">
                    <div className="w-10 h-10 rounded-full bg-[#007aff] text-white flex items-center justify-center font-semibold text-lg">{contact.name.charAt(0).toUpperCase()}</div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-[#1d1d1f]">{contact.name}</div>
                      {postcodes.length > 1 && <div className="text-xs text-[#86868b]">{postcodes.length} addresses</div>}
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#c7c7cc]" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Postcodes List */}
        {step === 3 && selectedContact && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="p-4 border-b border-[rgba(0,0,0,0.08)] flex items-center gap-3">
              <button onClick={() => { setSelectedContact(null); setStep(2); }} className="p-2 -ml-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"><ChevronLeft className="w-5 h-5 text-[#007aff]" /></button>
              <div>
                <DialogTitle className="text-lg font-semibold text-[#1d1d1f]">{selectedContact.name}</DialogTitle>
                <p className="text-sm text-[#86868b]">Select an address</p>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {(selectedContact.postcodes || [selectedContact.postcode]).map((postcode, idx) => {
                const isInUse = destinations.some(d => d.postcode.toLowerCase() === postcode.toLowerCase());
                return (
                  <button key={idx} onClick={() => { setSelectedPostcode(postcode); setStep(4); }}
                    className="w-full p-4 flex items-center gap-3 border-b border-[rgba(0,0,0,0.06)] last:border-0 transition-colors hover:bg-[#f5f5f7]">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#007aff]/10">
                      <MapPin className="w-5 h-5 text-[#007aff]" />
                    </div>
                    <div className="flex-1 text-left"><div className="font-medium text-[#1d1d1f]">{postcode}</div></div>
                    {isInUse && <span className="text-xs bg-[#ff9500]/10 text-[#ff9500] px-2 py-1 rounded-full font-medium">In Route</span>}
                    <ArrowRight className="w-5 h-5 text-[#c7c7cc]" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && selectedContact && selectedPostcode && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="p-4 border-b border-[rgba(0,0,0,0.08)] flex items-center gap-3">
              <button onClick={() => { setSelectedPostcode(null); const postcodes = selectedContact.postcodes || [selectedContact.postcode]; if (postcodes.length > 1) { setStep(3); } else { setSelectedContact(null); setStep(2); } }}
                className="p-2 -ml-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"><ChevronLeft className="w-5 h-5 text-[#007aff]" /></button>
              <div>
                <DialogTitle className="text-lg font-semibold text-[#1d1d1f]">{isHistoryMode ? 'Add to Past Route' : 'Add to Route'}</DialogTitle>
                <p className="text-sm text-[#86868b]">Confirm destination</p>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-[#f5f5f7] rounded-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#007aff] text-white flex items-center justify-center font-semibold text-xl">{selectedContact.name.charAt(0).toUpperCase()}</div>
                <div>
                  <div className="font-semibold text-[#1d1d1f]">{selectedContact.name}</div>
                  <div className="text-sm text-[#86868b]">{selectedPostcode}</div>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-[#1d1d1f]">Note (optional)</Label>
                <textarea placeholder="Add a note for the driver..." value={alphabetNote} onChange={(e) => setAlphabetNote(e.target.value)}
                  className="w-full h-20 px-4 py-3 bg-[#f5f5f7] rounded-xl text-base resize-none focus:outline-none focus:ring-2 focus:ring-[#007aff] placeholder:text-[#86868b]" />
              </div>
              <Button onClick={() => onConfirm()} className="w-full h-14 text-base font-semibold bg-[#34c759] hover:bg-[#2db84d] rounded-xl transition-all duration-200">
                <Plus className="w-5 h-5 mr-2" />{isHistoryMode ? 'Add to This Route' : 'Add to Route'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Manual entry */}
        {step === 5 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-200">
            <div className="p-4 border-b border-[rgba(0,0,0,0.08)] flex items-center gap-3">
              <button onClick={() => { setStep(1); }} className="p-2 -ml-2 rounded-xl hover:bg-[#f5f5f7] transition-colors"><ChevronLeft className="w-5 h-5 text-[#007aff]" /></button>
              <div>
                <DialogTitle className="text-lg font-semibold text-[#1d1d1f]">Manual Entry</DialogTitle>
                <p className="text-sm text-[#86868b]">Saved to your contacts automatically</p>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-[#1d1d1f]">Name <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  placeholder="e.g. Mrs Smith"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  className="h-12"
                  data-testid="manual-name-input"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-[#1d1d1f]">Postcode <span className="text-destructive">*</span></Label>
                <Input
                  type="text"
                  placeholder="e.g. SW1A 1AA"
                  value={manualPostcode}
                  onChange={(e) => setManualPostcode(e.target.value)}
                  className="h-12 font-mono"
                  data-testid="manual-postcode-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-[#1d1d1f]">Phone (optional)</Label>
                <Input
                  type="tel"
                  placeholder="e.g. 07700 900123"
                  value={manualPhone}
                  onChange={(e) => setManualPhone(e.target.value)}
                  className="h-12"
                  data-testid="manual-phone-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5 block text-[#1d1d1f]">Note (optional)</Label>
                <textarea placeholder="Add a note for the driver..." value={alphabetNote} onChange={(e) => setAlphabetNote(e.target.value)}
                  className="w-full h-20 px-4 py-3 bg-[#f5f5f7] rounded-xl text-base resize-none focus:outline-none focus:ring-2 focus:ring-[#007aff] placeholder:text-[#86868b]" />
              </div>
              <Button
                onClick={submitManual}
                disabled={!manualName.trim() || !manualPostcode.trim()}
                className="w-full h-14 text-base font-semibold bg-[#34c759] hover:bg-[#2db84d] rounded-xl transition-all duration-200"
                data-testid="manual-submit-btn"
              >
                <Plus className="w-5 h-5 mr-2" />{isHistoryMode ? 'Add to This Route' : 'Add to Route'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
